import { api } from '../client';

// Types
export type CertificateType =
  | 'MTR'
  | 'COC'
  | 'COA'
  | 'CALIBRATION'
  | 'SAFETY'
  | 'ENVIRONMENTAL'
  | 'QUALITY'
  | 'OTHER';

export type CertificateStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'PENDING_RENEWAL';
export type ExpiryStatus = 'VALID' | 'EXPIRING_SOON' | 'EXPIRED' | 'DISPOSED';

export interface CertificateItem {
  id: string;
  certificateId: string;
  itemId?: string;
  item?: {
    id: string;
    name: string;
    sku: string;
  };
  equipmentId?: string;
  equipment?: {
    id: string;
    assetNumber: string;
    name: string;
  };
  batchNumber?: string;
  serialNumber?: string;
  createdAt: string;
}

export interface Certificate {
  id: string;
  companyId: string;
  certificateNumber: string;
  type: CertificateType;
  name: string;
  issuedBy: string;
  issuedDate: string;
  expiryDate?: string;
  status: CertificateStatus;
  documentUrl?: string;
  notes?: string;
  items?: CertificateItem[];
  _count?: {
    items: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MaterialExpiry {
  id: string;
  companyId: string;
  itemId: string;
  item: {
    id: string;
    name: string;
    sku: string;
  };
  batchNumber?: string;
  expiryDate: string;
  quantity: number;
  locationId: string;
  location: {
    id: string;
    name: string;
    code: string;
  };
  alertSent: boolean;
  status: ExpiryStatus;
  daysUntilExpiry?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CertificatesDashboard {
  certificates: {
    total: number;
    active: number;
    expiring: number;
    expired: number;
  };
  materialExpiry: {
    total: number;
    expiring: number;
    expired: number;
  };
  recentCertificates: Certificate[];
}

export interface QueryCertificateParams {
  type?: CertificateType;
  status?: CertificateStatus;
  search?: string;
  expiryBefore?: string;
  expiryAfter?: string;
  page?: number;
  limit?: number;
}

export interface QueryMaterialExpiryParams {
  itemId?: string;
  locationId?: string;
  status?: ExpiryStatus;
  daysUntilExpiry?: number;
  page?: number;
  limit?: number;
}

export interface CreateCertificateDto {
  type: CertificateType;
  name: string;
  issuedBy: string;
  issuedDate: string;
  expiryDate?: string;
  documentUrl?: string;
  notes?: string;
}

export interface UpdateCertificateDto extends Partial<CreateCertificateDto> {
  status?: CertificateStatus;
}

export interface LinkItemDto {
  itemId?: string;
  equipmentId?: string;
  batchNumber?: string;
  serialNumber?: string;
}

export interface CreateMaterialExpiryDto {
  itemId: string;
  batchNumber?: string;
  expiryDate: string;
  locationId: string;
}

export interface UpdateMaterialExpiryDto {
  expiryDate?: string;
  status?: ExpiryStatus;
}

// API Functions
export const certificatesApi = {
  // Certificates
  getAll: async (params?: QueryCertificateParams) => {
    const response = await api.get<{
      data: Certificate[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('/api/certificates', { params });
    return response.data;
  },

  getDashboard: async () => {
    const response = await api.get<CertificatesDashboard>('/api/certificates/dashboard');
    return response.data;
  },

  getExpiring: async (days?: number) => {
    const response = await api.get<Certificate[]>('/api/certificates/expiring', {
      params: days ? { days } : undefined,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<Certificate>(`/api/certificates/${id}`);
    return response.data;
  },

  create: async (data: CreateCertificateDto) => {
    const response = await api.post<Certificate>('/api/certificates', data);
    return response.data;
  },

  update: async (id: string, data: UpdateCertificateDto) => {
    const response = await api.patch<Certificate>(`/api/certificates/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/api/certificates/${id}`);
    return response.data;
  },

  linkItem: async (certificateId: string, data: LinkItemDto) => {
    const response = await api.post<CertificateItem>(`/api/certificates/${certificateId}/items`, data);
    return response.data;
  },

  unlinkItem: async (certificateId: string, itemId: string) => {
    const response = await api.delete(`/api/certificates/${certificateId}/items/${itemId}`);
    return response.data;
  },

  // Material Expiry
  getMaterialExpiries: async (params?: QueryMaterialExpiryParams) => {
    const response = await api.get<{
      data: MaterialExpiry[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('/api/certificates/material-expiry', { params });
    return response.data;
  },

  createMaterialExpiry: async (data: CreateMaterialExpiryDto) => {
    const response = await api.post<MaterialExpiry>('/api/certificates/material-expiry', data);
    return response.data;
  },

  updateMaterialExpiry: async (id: string, data: UpdateMaterialExpiryDto) => {
    const response = await api.patch<MaterialExpiry>(`/api/certificates/material-expiry/${id}`, data);
    return response.data;
  },
};
